/**
 * Budgets: some categories and tags, a figure, and a period to reach it in.
 *
 * A plan is an appointment — "something in Electricity, around the 2nd" — and
 * can say a bill never turned up. A budget is a ceiling, or a target: it has no
 * day at all, so nothing can be late. Every transaction it covers over the
 * whole period is added up and measured against one figure, and the only thing
 * it can say is how full it is.
 *
 * A budget covers a transaction filed under any of its `categories` (names) or
 * wearing any of its `tags` (ids) — any of them, not all, and once however
 * many it matches. Nothing is stored against a transaction here either; the
 * categories and tags are the link, worked out fresh every time.
 *
 * Which tags a transaction wears is not this module's business: a category's
 * tags are inherited, and only the store knows that. So it is asked for, as
 * `tagsOf(tx)`, and defaults to none.
 */
import { addDays, diffDays, endOfMonth, fromISO, monthOf } from './dates'
import { round2 } from './money'
import { CADENCES, stepBy } from './recurrence'

/** Every cadence but the one-off: a budget with no repeat is just a total. */
export const BUDGET_CADENCES = CADENCES.filter((c) => c.value !== 'once')

/**
 * Every state a budget's period can be in, with the label and the colour that
 * go with it. The tone lives here rather than in the component so the row, the
 * bar, the panel's count and the alert feed cannot disagree about how bad a
 * thing is.
 *
 * The three bands are what a tolerance is for. Below the figure there is
 * nothing to say — green; passing it at all is worth seeing — amber; passing it
 * by more than the padding you allowed is the alarm you set — red.
 */
export const BUDGET_STATUS = {
  upcoming: { tone: 'muted' },
  // An automatic budget with no earlier period to learn from has no figure,
  // and a figure of nothing would call every purchase an overspend.
  learning: { tone: 'muted' },
  // Inside the figure there is nothing wrong, so it reads like anything else
  // that is fine. Amber and red are what a budget has to say for itself.
  running: { tone: 'good' },
  // A spending limit, going the wrong way.
  over: { tone: 'warn' },
  limit: { tone: 'danger' },
  // An income target, going the wrong way.
  short: { tone: 'warn' },
  missed: { tone: 'danger' },
  // Finished, and the right side of the figure.
  under: { tone: 'good' },
  matched: { tone: 'good', income: true },
}

/**
 * The catalogue key for what the row says. A target that was met reads
 * differently from a limit that was kept, so the income case has its own line.
 */
export const budgetLabelKey = (row) => {
  const status = BUDGET_STATUS[row?.status]
  if (!status) return ''
  return row.type === 'income' && status.income
    ? `budgetStatus.${row.status}.income`
    : `budgetStatus.${row.status}`
}

/** The colour the row, the bar and the alert all take. */
export const budgetTone = (row) => BUDGET_STATUS[row?.status]?.tone ?? 'muted'

/** Whether this budget is off the way it was set up to warn about. */
export const alarming = (row) => {
  const tone = budgetTone(row)
  return tone === 'warn' || tone === 'danger'
}

const signMatches = (tx, budget) => (budget.type === 'income' ? tx.amount > 0 : tx.amount < 0)

const noTags = () => []

/** Whether the budget covers this transaction at all, whichever way it went. */
export function covers(tx, budget, tagsOf = noTags) {
  const categories = budget?.categories ?? []
  const tags = budget?.tags ?? []
  if (tx.category && categories.some((name) => name.toLowerCase() === tx.category.toLowerCase())) {
    return true
  }
  if (!tags.length) return false
  const worn = tagsOf(tx)
  return tags.some((id) => worn.includes(id))
}

/** This transaction is one of the ones this budget is counting. */
export const countsToward = (tx, budget, tagsOf = noTags) =>
  !!budget && !budget.archived && signMatches(tx, budget) && covers(tx, budget, tagsOf)

/** Roughly which period a date falls in — corrected below, never trusted raw. */
function periodGuess(budget, date) {
  const start = fromISO(budget.start_date)
  const target = fromISO(date)
  const days = diffDays(budget.start_date, date)
  if (budget.cadence === 'weekly') return Math.floor(days / 7)
  if (budget.cadence === 'fortnightly') return Math.floor(days / 14)
  const months =
    (target.getFullYear() - start.getFullYear()) * 12 + (target.getMonth() - start.getMonth())
  const step = { monthly: 1, quarterly: 3, yearly: 12 }[budget.cadence] ?? 1
  return Math.floor(months / step)
}

/**
 * The period of `budget` that `date` falls in: `{ index, from, to }`.
 *
 * The guess is corrected by walking, because a month is not a fixed length and
 * a start that is not the first of its month puts the boundary mid-month.
 */
export function periodOf(budget, date) {
  if (!budget || !date || !budget.start_date) return null
  const at = (n) => stepBy(budget.cadence, budget.start_date, n)
  let index = periodGuess(budget, date)
  for (let guard = 0; guard < 8 && at(index) > date; guard++) index--
  for (let guard = 0; guard < 8 && at(index + 1) <= date; guard++) index++
  return { index, from: at(index), to: addDays(at(index + 1), -1) }
}

/**
 * Every period of a budget the window touches, counted **whole**: the budget is
 * about the month, not about the part of it you happen to be looking at.
 */
export function periodsIn(budget, from, to) {
  const out = []
  if (!budget || budget.archived || !budget.start_date) return out
  const stop = budget.end_date && budget.end_date < to ? budget.end_date : to
  if (budget.start_date > stop) return out
  const first = periodOf(budget, from > budget.start_date ? from : budget.start_date)
  if (!first) return out
  const at = (n) => stepBy(budget.cadence, budget.start_date, n)
  for (let index = first.index, guard = 0; guard < 400; index++, guard++) {
    const period = { from: at(index), to: addDays(at(index + 1), -1) }
    if (period.from > stop) break
    out.push(period)
  }
  return out
}

/** How many earlier periods an automatic budget averages, when it does not say. */
export const AUTO_PERIODS_DEFAULT = 3

/** The same number, kept to something sensible whatever was stored. */
export const autoPeriodsOf = (budget) => {
  const n = Math.round(Number(budget?.auto_periods))
  return Number.isFinite(n) && n >= 1 ? Math.min(n, 120) : AUTO_PERIODS_DEFAULT
}

/**
 * What an automatic budget expects of `period`: the average of what it covered
 * over the N periods before it — `{ amount, basis }`, basis being how many
 * periods the average is actually over.
 *
 * Only periods the ledger reaches count. Before the first transaction there is
 * no history, only silence, and averaging silence in would drag the figure
 * down for as long as the ledger is younger than N periods. So a monthly
 * budget on a two-month-old ledger averages two months, and one on an empty
 * ledger has a basis of 0 and no figure.
 *
 * Periods before the budget's own start date count: the budget is new, the
 * spending is not.
 *
 * @param {object[]} mine  the transactions the budget counts, already filtered
 * @param {string|null} since  the ledger's earliest date
 */
export function autoFigure(budget, period, mine, since) {
  const at = (n) => stepBy(budget.cadence, budget.start_date, n)
  const here = periodOf(budget, period.from)
  if (!here || !since) return { amount: 0, basis: 0 }
  let total = 0
  let basis = 0
  for (let back = 1; back <= autoPeriodsOf(budget); back++) {
    const from = at(here.index - back)
    const to = addDays(at(here.index - back + 1), -1)
    if (to < since) break
    basis++
    for (const tx of mine) if (tx.date >= from && tx.date <= to) total += Math.abs(tx.amount)
  }
  return { amount: basis ? round2(total / basis) : 0, basis }
}

/** A whole calendar month says its name; anything else says its two ends. */
export const isWholeMonth = (period) =>
  period.from.endsWith('-01') &&
  monthOf(period.from) === monthOf(period.to) &&
  period.to === endOfMonth(monthOf(period.to))

/**
 * One row per period of every live budget, holding the transactions counted
 * towards it and how full it is.
 *
 * Inside a period the total can only grow, so the only verdict available before
 * it ends is that the figure has already been passed. Everything else waits for
 * the period to be over.
 *
 * `tagsOf(tx)` is every tag a transaction wears, inherited ones included.
 * `since` is the ledger's first date, when `transactions` is not the whole of it.
 */
export function buildBudgets(budgets, transactions, { from, to, today, tagsOf = noTags, since: known }) {
  const rows = []
  // Where the ledger begins, which is where an automatic budget's memory does.
  // Given, when the rows are only a window of the ledger (stores/index.js);
  // otherwise the rows are the ledger, and say so themselves.
  let since = known ?? null
  if (known === undefined) {
    for (const tx of transactions) if (!tx.saving && (!since || tx.date < since)) since = tx.date
  }
  for (const budget of budgets ?? []) {
    if (budget.archived) continue
    const mine = transactions.filter((tx) => countsToward(tx, budget, tagsOf))
    for (const period of periodsIn(budget, from, to)) {
      const matches = mine.filter((tx) => tx.date >= period.from && tx.date <= period.to)
      const actual = matches.reduce((sum, t) => sum + Math.abs(t.amount), 0)
      const auto = budget.auto ? autoFigure(budget, period, mine, since) : null
      const expected = auto ? auto.amount : Math.abs(budget.amount)
      const tolerance = Math.max(0, budget.tolerance ?? 0.1)
      const variance = actual - expected
      const room = expected * tolerance
      const running = period.to >= today
      const started = period.from <= today

      // A limit can be judged the moment it is passed, because the total only
      // grows: amber past the figure, red past the padding. A target cannot —
      // being short of it halfway through a month is not news — so it waits for
      // the period to finish before saying anything but "on its way".
      let status
      if (!started) status = 'upcoming'
      else if (auto && !auto.basis) status = 'learning'
      else if (budget.type === 'income') {
        if (variance >= 0) status = 'matched'
        else if (running) status = 'running'
        else status = variance < -room ? 'missed' : 'short'
      } else if (variance > room) status = 'limit'
      else if (variance > 0) status = 'over'
      else if (running) status = 'running'
      else status = variance < -room ? 'under' : 'matched'

      rows.push({
        id: `${budget.id}:${period.from}`,
        budget,
        budgetId: budget.id,
        type: budget.type === 'income' ? 'income' : 'expense',
        categories: budget.categories ?? [],
        tags: budget.tags ?? [],
        from: period.from,
        to: period.to,
        expected,
        // Set on automatic budgets: how many earlier periods `expected` is the
        // average of. Null on a budget with a figure of its own.
        autoBasis: auto ? auto.basis : null,
        actual,
        variance,
        tolerance,
        // What is left of it — nothing once the period has gone by, because
        // unspent budget is not money owed to anyone.
        remaining: running ? Math.max(0, expected - actual) : 0,
        progress: expected > 0 ? Math.min(1, actual / expected) : 0,
        matches,
        running,
        status,
      })
    }
  }
  return rows.sort(
    (a, b) =>
      a.from.localeCompare(b.from) ||
      (a.categories[0] ?? '').localeCompare(b.categories[0] ?? '') ||
      a.budgetId.localeCompare(b.budgetId),
  )
}
